import { useState } from 'react';
import { Wand, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { apiClient } from '@/lib/api-client';
import { getApiErrorMessage } from '@/lib/api-error';
import { useWizardStore } from '../lib/wizard-store';

async function callDeriveFormMessage(body: {
  email01_text: string;
  sender_context: {
    name?: string;
    company?: string;
    offer_title?: string;
    offer_description?: string;
  };
  target_pitch: string;
  language: string;
}): Promise<string> {
  // Use apiClient (Bearer JWT V2) instead of raw fetch + cookies — same fix
  // as W6 BIG.call panel and W7 LeadScrape AI; cookie-only auth doesn't
  // work end-to-end in V2 since the access token lives in localStorage.
  const { data } = await apiClient.post<{ derived_message: string }>(
    '/v1/llm/derive-form-message',
    body,
  );
  return data.derived_message;
}

const DERIVE_ERROR_CODES = {
  ai_not_configured: "L'IA n'est pas configurée.",
  ERR_VALIDATION: 'Champs invalides — vérifie email01 et le contexte expéditeur.',
};

export function W4_FormFillMessage() {
  const {
    form_message_template,
    form_message_optimize_per_contact,
    setFormMessageTemplate,
    setFormMessageOptimizePerContact,
    email_templates,
    sender_first_name,
    sender_company_name,
    offer_title,
    offer_description,
    target_country,
    language,
  } = useWizardStore();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const email01 = email_templates.email01;
  const hasEmail01 = email01.trim().length > 0;

  async function derive() {
    setError(null);
    // Pre-flight checks so the API never sees an empty body and the user
    // gets an actionable error immediately rather than a generic 400.
    const trimmedEmail01 = email01.trim();
    if (trimmedEmail01.length === 0) {
      setError(
        "L'email01 est vide — édite ou réinitialise le template à l'étape 'Templates email' avant de dériver le message form-fill.",
      );
      return;
    }
    if (sender_company_name.trim().length === 0) {
      setError(
        "Le nom de l'entreprise expéditrice est vide — renseigne-le à l'étape 'Expéditeur, offre & cible'.",
      );
      return;
    }
    setLoading(true);
    try {
      const msg = await callDeriveFormMessage({
        email01_text: trimmedEmail01,
        sender_context: {
          name: sender_first_name || undefined,
          company: sender_company_name || undefined,
          offer_title: offer_title || undefined,
          offer_description: offer_description || undefined,
        },
        target_pitch: target_country || '',
        language: language || 'fr',
      });
      setFormMessageTemplate(msg);
    } catch (err) {
      setError(getApiErrorMessage(err, DERIVE_ERROR_CODES));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Card>
      <CardHeader
        title="Message form-fill"
        subtitle="Message court envoyé dans le champ message des formulaires de contact."
      />
      <CardContent className="space-y-4" data-testid="w4-formfill-section">
        <textarea
          value={form_message_template}
          onChange={(e) => setFormMessageTemplate(e.target.value)}
          rows={6}
          placeholder="Bonjour {firstname}, je vous contacte au sujet de..."
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm
                     focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary/30
                     placeholder:text-gray-400 resize-y"
          data-testid="form-message-textarea"
        />

        {error && (
          <p className="text-sm text-red-600" data-testid="derive-error">
            {error}
          </p>
        )}

        <div className="flex gap-2">
          <Button
            onClick={derive}
            disabled={!hasEmail01 || loading}
            data-testid="derive-from-mail01-btn"
          >
            <Wand size={16} />
            {loading ? 'Dérivation…' : 'Dériver depuis mail01'}
          </Button>
          <Button
            onClick={derive}
            variant="secondary"
            disabled={!hasEmail01 || loading}
            data-testid="regenerate-btn"
          >
            <RefreshCw size={16} />
            Régénérer
          </Button>
        </div>

        <Checkbox
          id="optimize-per-contact"
          checked={form_message_optimize_per_contact}
          onChange={(e) => setFormMessageOptimizePerContact(e.target.checked)}
          label="Optimiser par contact via IA au moment du form-fill"
          description="Réinvoque l'IA juste avant chaque submit avec les data du prospect (industry, services). Cible 95%+ de pertinence vs message générique."
          data-testid="optimize-per-contact-checkbox"
        />
      </CardContent>
    </Card>
  );
}
